import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { parkingApi, usePkList, usePkMutation, type PensionStatus, type PkPension } from "../api/parkingApi";
import { ConsolePage, DetailRow, Drawer, FilterSelect, Initials, KpiRow, KpiTile, LoadState, SearchBox, StatusChip, ToolButton, formatDateTime, relativeTime, toneOf, useTicker } from "../components/console/Console";
import FormModal, { toLocalInput, type FormValues } from "../components/console/FormModal";
import { LotSelect, PENSION_TONE, PlateBadge, errorText, money } from "./parkingUi";

// ===========================================================================
// Control de estacionamientos → Pensiones: abonados mensuales con vigencia,
// placas (pasan la pluma sin ticket), tarjeta o TAG, cajón asignado y cobro de
// la renovación (efectivo o terminal dentro del corte; Stripe o liga de pago).
// ===========================================================================

const STATES: PensionStatus[] = ["active", "expiring", "expired", "scheduled", "suspended", "cancelled"];

export default function PensionsScreen() {
  const { t, i18n } = useTranslation("parking");
  const now = useTicker(60_000);
  const [lot, setLot] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<PkPension | null>(null);
  const [edit, setEdit] = useState<Partial<PkPension> | null>(null);
  const [renew, setRenew] = useState<PkPension | null>(null);
  const lotsQ = usePkList("lots");
  const q = usePkList("pensions", { lot_id: lot || undefined, search: search || undefined });
  const all = useMemo(() => q.data ?? [], [q.data]);
  const rows = status ? all.filter((p) => p.effective_status === status) : all;
  const count = (s: PensionStatus) => all.filter((p) => p.effective_status === s).length;
  const monthly = all.filter((p) => ["active", "expiring"].includes(p.effective_status)).reduce((a, p) => a + p.monthly_fee, 0);

  const save = usePkMutation(({ id, body }: { id?: string; body: Partial<PkPension> }) => (id ? parkingApi.update("pensions", id, body) : parkingApi.create("pensions", body)));
  const renewM = usePkMutation(({ id, body }: { id: string; body: Parameters<typeof parkingApi.renewPension>[1] }) => parkingApi.renewPension(id, body));

  const submitEdit = async (v: FormValues) => {
    const body = {
      ...v,
      plates: String(v.plates || "").split(/[,\s]+/).filter(Boolean),
      monthly_fee: Number(v.monthly_fee || 0),
      valid_from: new Date(String(v.valid_from)).toISOString(),
      valid_until: new Date(String(v.valid_until)).toISOString(),
    } as unknown as Partial<PkPension>;
    try {
      await save.mutateAsync({ id: edit?.id, body });
      toast.success(t("common.saved"));
      setEdit(null);
    } catch (err) {
      toast.error(errorText(err, t("common.saveError")));
    }
  };

  const submitRenew = async (v: FormValues) => {
    if (!renew) return;
    try {
      const out = await renewM.mutateAsync({ id: renew.id, body: { months: Number(v.months || 1), method: String(v.method) as "cash", received: v.received ? Number(v.received) : undefined, reference: String(v.reference || "") || undefined } });
      if (out.url) {
        await navigator.clipboard?.writeText(out.url).catch(() => undefined);
        toast.success(t("pensions.linkCopied"));
      } else toast.success(t("pensions.renewed"));
      setRenew(null);
      setSelected(null);
    } catch (err) {
      toast.error(errorText(err, t("common.saveError")));
    }
  };

  const lots = lotsQ.data ?? [];
  const startEdit = (p?: PkPension) =>
    setEdit(
      p ?? {
        lot_id: lot || lots[0]?.id,
        credential_type: "tag",
        status: "active",
        monthly_fee: 1500,
        valid_from: new Date().toISOString(),
        valid_until: new Date(Date.now() + 30 * 86400_000).toISOString(),
        plates: [],
      },
    );

  return (
    <ConsolePage
      icon="ri-vip-crown-line"
      title={t("pensions.title")}
      subtitle={t("pensions.subtitle")}
      actions={<ToolButton icon="ri-add-line" variant="primary" onClick={() => startEdit()}>{t("pensions.new")}</ToolButton>}
    >
      <KpiRow>
        <KpiTile icon="ri-checkbox-circle-line" tone="success" label={t("pensionStatus.active")} value={count("active")} />
        <KpiTile icon="ri-time-line" tone="warning" label={t("pensionStatus.expiring")} value={count("expiring")} />
        <KpiTile icon="ri-close-circle-line" tone="error" label={t("pensionStatus.expired")} value={count("expired")} />
        <KpiTile icon="ri-pause-circle-line" tone="neutral" label={t("pensionStatus.suspended")} value={count("suspended")} />
        <KpiTile icon="ri-calendar-event-line" tone="info" label={t("pensionStatus.scheduled")} value={count("scheduled")} />
        <KpiTile icon="ri-money-dollar-circle-line" tone="primary" label={t("pensions.monthlyIncome")} value={money(monthly, i18n.language)} />
      </KpiRow>
      <div className="flex flex-wrap items-center gap-2">
        <SearchBox value={search} onChange={setSearch} placeholder={t("pensions.searchPlaceholder")} />
        <LotSelect lots={lots} value={lot} onChange={setLot} />
        <FilterSelect value={status} onChange={setStatus} label={t("common.status")} options={[{ value: "", label: t("common.allStatuses") }, ...STATES.map((s) => ({ value: s, label: t(`pensionStatus.${s}`) }))]} />
      </div>
      <LoadState loading={q.isLoading} error={q.error} empty={!rows.length} emptyText={t("pensions.empty")} onRetry={() => q.refetch()}>
        <div className="overflow-x-auto rounded-xl border border-line bg-card">
          <table className="w-full min-w-[880px] text-sm">
            <thead className="bg-subtle text-left text-[11px] uppercase tracking-wide text-content-muted">
              <tr>
                <th className="px-3 py-2">{t("pensions.holder")}</th>
                <th className="px-3 py-2">{t("pensions.plates")}</th>
                <th className="px-3 py-2">{t("common.lot")}</th>
                <th className="px-3 py-2">{t("pensions.credential")}</th>
                <th className="px-3 py-2 text-right">{t("pensions.fee")}</th>
                <th className="px-3 py-2">{t("pensions.validUntil")}</th>
                <th className="px-3 py-2">{t("common.status")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {rows.map((p) => (
                <tr key={p.id} className="cursor-pointer hover:bg-subtle" onClick={() => setSelected(p)}>
                  <td className="px-3 py-2">
                    <span className="flex items-center gap-2">
                      <Initials name={p.holder_name} size="sm" />
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-content">{p.holder_name}</span>
                        {p.company_name && <span className="block truncate text-[11px] text-content-muted">{p.company_name}</span>}
                      </span>
                    </span>
                  </td>
                  <td className="px-3 py-2"><span className="flex flex-wrap gap-1">{p.plates.map((pl) => <PlateBadge key={pl} plate={pl} />)}</span></td>
                  <td className="max-w-[200px] truncate px-3 py-2 text-content-secondary">{p.lot_name}{p.space_code ? ` · ${p.space_code}` : ""}</td>
                  <td className="px-3 py-2 font-mono text-xs text-content-secondary">{t(`credential.${p.credential_type}`)} {p.credential_number}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-content">{money(p.monthly_fee, i18n.language)}</td>
                  <td className="px-3 py-2 text-content-secondary" title={formatDateTime(p.valid_until, i18n.language)}>{relativeTime(p.valid_until, i18n.language, now)}</td>
                  <td className="px-3 py-2"><StatusChip tone={toneOf(PENSION_TONE, p.effective_status)}>{t(`pensionStatus.${p.effective_status}`)}</StatusChip></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </LoadState>

      <Drawer
        open={Boolean(selected)}
        title={selected?.holder_name ?? ""}
        subtitle={selected?.company_name ?? selected?.lot_name}
        onClose={() => setSelected(null)}
        footer={
          selected && (
            <>
              <ToolButton icon="ri-edit-line" onClick={() => startEdit(selected)}>{t("common.edit")}</ToolButton>
              {selected.status === "active" && <ToolButton icon="ri-pause-line" onClick={() => save.mutateAsync({ id: selected.id, body: { status: "suspended" } }).then(() => { toast.success(t("common.saved")); setSelected(null); })}>{t("pensions.suspend")}</ToolButton>}
              {selected.status !== "cancelled" && <ToolButton icon="ri-refresh-line" variant="primary" onClick={() => setRenew(selected)}>{t("pensions.renew")}</ToolButton>}
            </>
          )
        }
      >
        {selected && (
          <div>
            <div className="mb-4 flex items-center gap-3">
              <Initials name={selected.holder_name} size="lg" />
              <div>
                <StatusChip tone={toneOf(PENSION_TONE, selected.effective_status)}>{t(`pensionStatus.${selected.effective_status}`)}</StatusChip>
                <p className="mt-1 text-xs text-content-secondary">{selected.holder_email} · {selected.holder_phone}</p>
              </div>
            </div>
            <DetailRow label={t("pensions.plates")}><span className="flex flex-wrap justify-end gap-1">{selected.plates.map((pl) => <PlateBadge key={pl} plate={pl} />)}</span></DetailRow>
            <DetailRow label={t("common.lot")}>{selected.lot_name}</DetailRow>
            <DetailRow label={t("tickets.space")}>{selected.space_code ?? t("pensions.noSpace")}</DetailRow>
            <DetailRow label={t("pensions.credential")}>{t(`credential.${selected.credential_type}`)} {selected.credential_number}</DetailRow>
            <DetailRow label={t("pensions.fee")}>{money(selected.monthly_fee, i18n.language)}</DetailRow>
            <DetailRow label={t("pensions.validFrom")}>{formatDateTime(selected.valid_from, i18n.language)}</DetailRow>
            <DetailRow label={t("pensions.validUntil")}>{formatDateTime(selected.valid_until, i18n.language)}</DetailRow>
            <DetailRow label={t("pensions.lastPayment")}>{selected.last_payment_at ? formatDateTime(selected.last_payment_at, i18n.language) : "—"}</DetailRow>
            <p className="mt-3 rounded-lg bg-info/10 p-2 text-xs text-content-secondary"><i className="ri-information-line" /> {t("pensions.gateHint")}</p>
            {selected.notes && <p className="mt-2 text-xs text-content-muted">{selected.notes}</p>}
          </div>
        )}
      </Drawer>

      {edit && (
        <FormModal
          title={edit.id ? t("pensions.edit") : t("pensions.new")}
          submitLabel={t("common.save")}
          busy={save.isPending}
          onClose={() => setEdit(null)}
          onSubmit={submitEdit}
          initial={{
            ...(edit as unknown as FormValues),
            plates: (edit.plates ?? []).join(", "),
            valid_from: toLocalInput(new Date(edit.valid_from ?? Date.now())),
            valid_until: toLocalInput(new Date(edit.valid_until ?? Date.now())),
          }}
          fields={[
            { key: "holder_name", label: t("pensions.holder"), required: true },
            { key: "company_name", label: t("pensions.company") },
            { key: "holder_email", label: t("pensions.email") },
            { key: "holder_phone", label: t("pensions.phone") },
            { key: "plates", label: t("pensions.plates"), required: true, hint: t("pensions.platesHint"), full: true },
            ...(edit.id ? [] : [{ key: "lot_id", label: t("common.lot"), type: "select" as const, required: true, options: lots.map((l) => ({ value: l.id, label: l.name })) }]),
            { key: "monthly_fee", label: t("pensions.fee"), type: "number", min: 0, required: true },
            { key: "credential_type", label: t("pensions.credentialType"), type: "select", required: true, options: (["tag", "card", "plate"] as const).map((v) => ({ value: v, label: t(`credential.${v}`) })) },
            { key: "credential_number", label: t("pensions.credentialNumber") },
            { key: "valid_from", label: t("pensions.validFrom"), type: "datetime", required: true },
            { key: "valid_until", label: t("pensions.validUntil"), type: "datetime", required: true },
            { key: "notes", label: t("pensions.notes"), type: "textarea" },
          ]}
        />
      )}
      {renew && (
        <FormModal
          title={t("pensions.renewTitle", { interpolation: { escapeValue: false }, name: renew.holder_name })}
          submitLabel={t("pensions.renew")}
          busy={renewM.isPending}
          onClose={() => setRenew(null)}
          onSubmit={submitRenew}
          initial={{ months: 1, method: "cash" }}
          fields={[
            { key: "months", label: t("pensions.months"), type: "number", min: 1, max: 12, required: true, hint: t("pensions.monthsHint", { interpolation: { escapeValue: false }, fee: money(renew.monthly_fee, i18n.language) }) },
            { key: "method", label: t("tickets.method"), type: "select", required: true, options: (["cash", "card_terminal", "stripe", "link"] as const).map((m) => ({ value: m, label: t(`method.${m}`) })) },
            { key: "received", label: t("cashier.received"), type: "number", min: 0, hint: t("pensions.receivedHint") },
            { key: "reference", label: t("cashier.reference") },
          ]}
        />
      )}
    </ConsolePage>
  );
}
